import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { Key } from '@lichess-org/chessground/types';
import { ChessgroundAdapter, type BoardInput } from '../board/chessground';
import { directionFromKey, moveKey } from '../board/keyboard';
import { useBoardTheme } from '../board/theme';
import { uciMovesToDests } from '../board/uci';
import { PromotionModal } from './PromotionModal';

export interface BoardViewProps {
  input: BoardInput;
  /** Return false if the move was declined, so the board can restore the position. */
  onMove: (uci: string) => boolean | void | Promise<boolean | void>;
  /** Accessible name for the board region, e.g. "Chessboard, White to move". */
  label: string;
  /** Keep the last position visible while a save or navigation loads. */
  busy?: boolean;
}

/**
 * The only board surface. It hosts Chessground (no custom renderer, §6.2),
 * drives it from the API FEN + legal UCI moves, and emits UCI candidates back.
 * Keyboard navigation: arrows move a focus cursor, Enter/Space select a piece
 * and play to a destination, Escape cancels.
 */
export function BoardView({ input, onMove, label, busy = false }: BoardViewProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const adapterRef = useRef<ChessgroundAdapter | null>(null);
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const inputRef = useRef(input);
  inputRef.current = input;
  // Squares restyle through CSS variables; arrows are redrawn in the new skin's brushes.
  const theme = useBoardTheme();
  const inputKey = JSON.stringify([
    input.fen, input.turnColor, input.orientation, input.dests, input.lastMove, input.arrows, theme.id,
  ]);

  const [promotionCandidates, setPromotionCandidates] = useState<string[] | null>(null);
  const [cursor, setCursor] = useState<Key | null>(null);
  const [origin, setOrigin] = useState<Key | null>(null);

  // Mount Chessground once.
  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;
    const adapter = new ChessgroundAdapter({
      onMove: async (uci) => {
        const sourceFen = inputRef.current.fen;
        try {
          const accepted = await onMoveRef.current(uci);
          if (accepted === false && inputRef.current.fen === sourceFen) adapter.cancelMove();
        } catch {
          if (inputRef.current.fen === sourceFen) adapter.cancelMove();
        }
      },
      onPromotion: (candidates) => setPromotionCandidates(candidates),
    });
    adapter.mount(element, inputRef.current);
    adapterRef.current = adapter;
    return () => {
      adapter.destroy();
      adapterRef.current = null;
    };
  }, []);

  // Reconfigure on every position/legal-move change; reset keyboard state.
  // Keyed on the serialized position so a fresh-but-equal input object is a no-op.
  useLayoutEffect(() => {
    adapterRef.current?.set(inputRef.current);
    setOrigin(null);
    setCursor(null);
    adapterRef.current?.setFocus(null);
  }, [inputKey]);

  const defaultSquare = useMemo<Key>(() => {
    const origins = [...uciMovesToDests(input.dests).keys()];
    return origins[0] ?? ('e2' as Key);
  }, [input.dests]);

  const orientation = input.orientation === 'b' ? 'black' : 'white';

  function handleActivate(adapter: ChessgroundAdapter, square: Key): void {
    if (origin === null) {
      if (adapter.destinationsFor(square).length > 0) {
        setOrigin(square);
        adapter.selectSquare(square);
      }
      return;
    }
    if (adapter.destinationsFor(origin).includes(square)) {
      adapter.attemptMove(origin, square);
      setOrigin(null);
      adapter.selectSquare(null);
    } else if (square === origin) {
      setOrigin(null);
      adapter.selectSquare(null);
    } else if (adapter.destinationsFor(square).length > 0) {
      setOrigin(square);
      adapter.selectSquare(square);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const adapter = adapterRef.current;
    if (!adapter) return;

    const direction = directionFromKey(event.key);
    if (direction !== null) {
      event.preventDefault();
      const square = cursor ?? defaultSquare;
      const next = moveKey(square, direction, orientation);
      setCursor(next);
      adapter.setFocus(next);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const square = cursor ?? defaultSquare;
      setCursor(square);
      handleActivate(adapter, square);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setOrigin(null);
      setCursor(null);
      adapter.selectSquare(null);
      adapter.setFocus(null);
    }
  }

  return (
    <>
      <div
        ref={containerRef}
        className={`cg-wrap board-container relative aspect-square w-full${busy ? ' board-container-busy' : ''}`}
        role="application"
        aria-label={label}
        aria-busy={busy}
        aria-describedby="board-status"
        tabIndex={busy ? -1 : 0}
        onKeyDown={busy ? undefined : handleKeyDown}
        data-testid="board"
      />
      <p id="board-status" className="sr-only" aria-live="polite">
        {cursor === null ? label : `${label} Keyboard cursor on ${cursor}.`}
      </p>
      <PromotionModal
        open={promotionCandidates !== null}
        candidates={promotionCandidates ?? []}
        onSelect={(role) => {
          adapterRef.current?.submitPromotion(role);
          setPromotionCandidates(null);
        }}
        onCancel={() => {
          adapterRef.current?.cancelPromotion();
          setPromotionCandidates(null);
        }}
      />
    </>
  );
}
